import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Truck, Users, Settings, DollarSign, CheckCircle } from 'lucide-react';

const STEPS = [
  {
    icon: <Truck size={36} color="#f97316" />,
    title: "Welcome to Daily Driver Pulse!",
    body: "You're all set up as an Admin. Let's walk you through 4 quick steps to get your company running.",
    step: "1 of 4",
  },
  {
    icon: <Users size={36} color="#f97316" />,
    title: "Add Your Customers",
    body: "You're in the Admin Dashboard. Go to the Customers tab to add each company you work for and the rate you charge them — by the hour, ton, or load. You must add customers before drivers can log work.",
    step: "2 of 4",
  },
  {
    icon: <Settings size={36} color="#f97316" />,
    title: "Share Your Company Code",
    body: "Go to the Settings tab and copy your Company Code. Share it with your drivers so they can sign up and link to your account automatically. Each driver will need this code when they register.",
    step: "3 of 4",
  },
  {
    icon: <CheckCircle size={36} color="#f97316" />,
    title: "You're Ready to Go!",
    body: "Your drivers can now submit logs from their phone. Check the Weekly Invoice tab to see invoices build automatically as logs come in. You can download or print invoices at any time.",
    step: "4 of 4",
  },
];

const STORAGE_KEY = 'ddp_onboarding_complete';

export default function OnboardingModal() {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) !== 'done') {
      setVisible(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'done');
    setVisible(false);
  };

  if (!visible) return null;

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: 32,
        maxWidth: 420,
        width: '100%',
        position: 'relative',
        boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
      }}>
        {/* X button */}
        <button onClick={handleDismiss} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', padding: 4,
        }}>
          <X size={18} />
        </button>

        {/* Step indicator dots */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === currentStep ? 20 : 6,
              height: 6,
              borderRadius: 3,
              background: i === currentStep ? 'var(--accent)' : 'var(--border)',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Icon */}
        <div style={{ marginBottom: 16 }}>{step.icon}</div>

        {/* Step label */}
        <div style={{
          fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'var(--accent)', marginBottom: 8,
        }}>
          Step {step.step}
        </div>

        {/* Title */}
        <h2 style={{
          fontFamily: 'Barlow Condensed', fontWeight: 800,
          fontSize: 22, marginBottom: 12, color: 'var(--text)',
        }}>
          {step.title}
        </h2>

        {/* Body */}
        <p style={{
          fontSize: 14, color: 'var(--text-muted)',
          lineHeight: 1.6, marginBottom: 28,
        }}>
          {step.body}
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          {currentStep > 0 && (
            <button onClick={() => setCurrentStep(s => s - 1)} className="btn-secondary" style={{ padding: '10px 20px' }}>
              ← Back
            </button>
          )}
          <button onClick={handleNext} className="btn-primary" style={{
            flex: 1, padding: '10px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {isLast ? (
              <><CheckCircle size={15} /> Get Started!</>
            ) : (
              <>Next <ChevronRight size={15} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
